// Centralized HTTP setup: inject Bearer tokens and silently refresh on 401

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const COGNITO_CLIENT_ID = process.env.REACT_APP_USER_POOL_CLIENT_ID;
const TOKEN_SKEW_MS = 60000; // 1 minute skew to avoid edge expiry

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function isJwtExpired(token) {
  if (!token) return true;
  const claims = parseJwt(token);
  if (!claims?.exp) return true;
  return claims.exp * 1000 <= (Date.now() + TOKEN_SKEW_MS);
}

function tokensExpired(tokens) {
  if (!tokens) return true;
  // If either refresh or id token is expired, treat as invalid to avoid refresh loops
  if (isJwtExpired(tokens.refresh_token)) return true;
  if (isJwtExpired(tokens.id_token)) return true;
  return false;
}

function isCognitoTokenEndpoint(url) {
  try {
    const u = new URL(typeof url === 'string' ? url : (url?.url || ''), window.location.origin);
    return u.hostname === COGNITO_DOMAIN && u.pathname === '/oauth2/token';
  } catch {
    return false;
  }
}

function getTokens() {
  try {
    const raw = localStorage.getItem('cognitoTokens');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (tokensExpired(parsed)) {
      localStorage.removeItem('cognitoTokens');
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
function setTokens(tok) {
  try {
    localStorage.setItem('cognitoTokens', JSON.stringify(tok));
    // Optionally notify listeners that tokens changed
    window.dispatchEvent(new Event('cognitoTokensUpdated'));
  } catch {}
}

function invalidateSession() {
  try { localStorage.removeItem('cognitoTokens'); } catch {}
  // Optionally broadcast for interested listeners
  try { window.dispatchEvent(new Event('auth:session-invalid')); } catch {}
  // Navigate to logged_out route to show friendly message
  const dest = `${window.location.origin}/logged_out`;
  window.location.assign(dest);
}

async function refreshTokens() {
  const tokens = getTokens();
  const refreshToken = tokens?.refresh_token;
  if (!refreshToken) return null;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: COGNITO_CLIENT_ID,
    refresh_token: refreshToken,
  });
  const resp = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) return null;
  const data = await resp.json();
  const updated = {
    id_token: data.id_token || tokens?.id_token,
    access_token: data.access_token || tokens?.access_token,
    refresh_token: data.refresh_token || tokens?.refresh_token,
    token_type: data.token_type || tokens?.token_type,
    expires_in: data.expires_in || tokens?.expires_in,
  };
  setTokens(updated);
  return updated;
}

let refreshPromise = null;
async function ensureFreshToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const updated = await refreshTokens();
    return updated;
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// Patch window.fetch: inject Authorization and retry once after silent refresh
if (typeof window !== 'undefined' && window.fetch) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}, _internal) => {
    const target = typeof input === 'string' ? input : input?.url;
    const skipAuth = isCognitoTokenEndpoint(target);
    const options = { ...(init || {}) };
    const headers = new Headers(options.headers || {});
    // Inject Authorization if not present
    if (!skipAuth && !headers.has('Authorization')) {
      const tok = getTokens();
      if (tok?.id_token) headers.set('Authorization', `Bearer ${tok.id_token}`);
    }
    options.headers = headers;
    let response = await originalFetch(input, options);
    // Do not intercept or retry Cognito token endpoint
    if (skipAuth) return response;
    if (response.status !== 401 || _internal === 'retry') {
      // If this is a retried request and still 401, invalidate session
      if (_internal === 'retry' && response.status === 401) invalidateSession();
      return response;
    }
    // Attempt silent refresh then retry once
    const updated = await ensureFreshToken();
    if (!updated?.id_token) return response; // give up; allow caller to handle
    const retryHeaders = new Headers(options.headers || {});
    retryHeaders.set('Authorization', `Bearer ${updated.id_token}`);
    const retryOptions = { ...options, headers: retryHeaders };
    const retried = await originalFetch(input, retryOptions, 'retry');
    if (retried.status === 401) invalidateSession();
    return retried;
  };
}

// Note: axios is optional. The app primarily uses fetch.
// If you add axios-based calls later, consider wiring similar
// interceptors here guarded by a dynamic check for window.axios.
